import { TableCellType } from "components/LazyTable";
import useGlobalContext from "hooks/globalData";
import { useQuery } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  ListRequestOfBaseFilter,
  ListResponseOfLocationType,
  ListResponseOfState,
  SkuAttributeExtended,
} from "services/KnowfullyAdminClient";
import {
  AttributeCodeConstants,
  AttributeMetaValues,
  LocationMetaValueNames,
  SponsorMetaValueNames,
  SponsorTypeNames,
} from "types/common-types";
import { AttributeWithId } from "types/sku-related-types";

type TranslateFn = (descriptor: { id: string }) => string;

type StateLookup = { id?: string; name?: string; stateName?: string };
type LocationTypeLookup = { id?: string; name?: string };

export type LocationLookups = {
  states?: StateLookup[];
  locationTypes?: LocationTypeLookup[];
};

const equalsIgnoreCase = (a?: string, b?: string) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

const getMetaValue = (attr: SkuAttributeExtended, name: string) =>
  attr.attributeMetaValues?.find((m) => m.name === name)?.value ?? "";

const getSkuAttributeMetaValue = (attr: SkuAttributeExtended, name: string) =>
  attr.skuAttributeMetaValues?.find((m) => m.name === name)?.value ?? "";

export const getAttributeCodeConstant = (
  attributes?: SkuAttributeExtended[]
) => attributes?.[0]?.attributeTypeCodeConstant?.toLowerCase();

export const hasLocationAttributeGroup = (
  groups?: { attributes?: SkuAttributeExtended[] }[]
) =>
  !!groups?.some(
    (group) =>
      getAttributeCodeConstant(group.attributes) ===
      AttributeCodeConstants.Locations
  );

export const useLocationLookups = (
  enabled: boolean = true
): LocationLookups => {
  const { stateService, locationTypeService } = useGlobalContext();

  const { data: statesData } = useQuery<ListResponseOfState>(
    ["stateService.getStates"],
    () =>
      stateService.getStates(
        ListRequestOfBaseFilter.fromJS({ sortField: "stateName" })
      ),
    { enabled }
  );

  const { data: locationTypesData } = useQuery<ListResponseOfLocationType>(
    ["locationTypeService.getLocationTypes"],
    () =>
      locationTypeService.getLocationTypes(
        AdminListRequestOfBaseFilter.fromJS({ sortField: "name" })
      ),
    { enabled }
  );

  return {
    states: statesData?.data,
    locationTypes: locationTypesData?.data,
  };
};

const resolveStateLabel = (
  attr: SkuAttributeExtended,
  states?: StateLookup[]
) => {
  const direct = getMetaValue(attr, LocationMetaValueNames.State);
  if (direct) return direct;

  const stateId = getMetaValue(attr, AttributeMetaValues.StateId);
  if (!stateId) return "";

  const state = states?.find((s) => equalsIgnoreCase(s.id, stateId));
  if (!state) return "";

  return state.name
    ? `${state.stateName} (${state.name})`
    : state.stateName ?? "";
};

const resolveLocationTypeLabel = (
  attr: SkuAttributeExtended,
  locationTypes?: LocationTypeLookup[]
) => {
  const direct = getMetaValue(attr, LocationMetaValueNames.LocationType);
  if (direct) return direct;

  const locationTypeId = getMetaValue(attr, AttributeMetaValues.LocationTypeId);
  if (!locationTypeId) return "";

  return (
    locationTypes?.find((lt) => equalsIgnoreCase(lt.id, locationTypeId))
      ?.name ?? ""
  );
};

export const flattenLocationMetaValues = (
  attributes: AttributeWithId[],
  lookups: LocationLookups = {}
) =>
  attributes.map((a) => ({
    ...a,
    company: getMetaValue(a, LocationMetaValueNames.Company),
    address1: getMetaValue(a, LocationMetaValueNames.Address1),
    city: getMetaValue(a, LocationMetaValueNames.City),
    state: resolveStateLabel(a, lookups.states),
    locationType: resolveLocationTypeLabel(a, lookups.locationTypes),
  }));

const resolveSponsorTypeLabel = (
  attr: SkuAttributeExtended,
  t: TranslateFn
) => {
  const value = getSkuAttributeMetaValue(attr, SponsorMetaValueNames.SponsorType);
  if (equalsIgnoreCase(value, SponsorTypeNames.JointProvider)) {
    return t({ id: "sponsorTypeJointProvider" });
  }
  if (equalsIgnoreCase(value, SponsorTypeNames.Sponsor)) {
    return t({ id: "sponsorTypeSponsor" });
  }
  return "";
};

export const flattenSponsorMetaValues = <T extends SkuAttributeExtended>(
  attributes: T[],
  t: TranslateFn
): (T & { sponsorTypeLabel: string })[] =>
  attributes.map((a) => ({
    ...a,
    sponsorTypeLabel: resolveSponsorTypeLabel(a, t),
  })) as (T & { sponsorTypeLabel: string })[];

export const buildAttributeDataCells = ({
  codeConstant,
  allowDates,
  defaultAccessor,
  t,
}: {
  codeConstant?: string;
  allowDates: boolean;
  defaultAccessor: string;
  t: TranslateFn;
}): Array<TableCellType> => {
  const cells: Array<TableCellType> = [];

  if (codeConstant === AttributeCodeConstants.Locations) {
    cells.push(
      {
        accessor: "displayText",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "locationNameLabel" }),
      },
      {
        accessor: "company",
        align: "left",
        width: 150,
        type: "text",
        label: t({ id: "companyLabel" }),
      },
      {
        accessor: "address1",
        align: "left",
        width: 150,
        type: "text",
        label: t({ id: "address1Label" }),
      },
      {
        accessor: "city",
        align: "left",
        width: 120,
        type: "text",
        label: t({ id: "cityLabel" }),
      },
      {
        accessor: "state",
        align: "left",
        width: 100,
        type: "text",
        label: t({ id: "locationStateLabel" }),
      },
      {
        accessor: "locationType",
        align: "left",
        width: 130,
        type: "text",
        label: t({ id: "locationTypeLabel" }),
      }
    );
  } else if (codeConstant === AttributeCodeConstants.Sponsors) {
    cells.push(
      {
        accessor: defaultAccessor,
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "skuAttributeLabel" }),
      },
      {
        accessor: "sponsorTypeLabel",
        align: "left",
        width: 150,
        type: "text",
        label: t({ id: "sponsorTypeLabel" }),
      }
    );
  } else {
    cells.push({
      accessor: defaultAccessor,
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuAttributeLabel" }),
    });
  }

  if (allowDates) {
    cells.push(
      {
        accessor: "startDate",
        align: "left",
        width: 150,
        type: "date",
        label: t({ id: "startDateLabel" }),
      },
      {
        accessor: "endDate",
        align: "left",
        width: 150,
        type: "date",
        label: t({ id: "endDateLabel" }),
      }
    );
  }

  return cells;
};
